import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { generateSecurePhotoPath, generateUserHash, generateSecureImageHash } from '@/utils/securePhotoNaming';
import { detectPhotoVersion } from '@/utils/photoVersionDetector';
import { useMultiSizeImageProcessor } from './useMultiSizeImageProcessor';
import { getSignedMediaUrl } from '@/utils/signedMediaUtils';

interface MigrationProgress {
  userId: string;
  username: string;
  status: 'pending' | 'analyzing' | 'downloading' | 'processing' | 'uploading' | 'updating' | 'cleaning' | 'completed' | 'error';
  message: string;
  progress: number; // 0-100
}

export function usePhotoMigrationV15() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<Map<string, MigrationProgress>>(new Map());
  const { processImageToVariants } = useMultiSizeImageProcessor();

  const updateProgress = useCallback((userId: string, update: Partial<MigrationProgress>) => {
    setProgress(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(userId) || { userId, username: '', status: 'pending', message: '', progress: 0 };
      newMap.set(userId, { ...current, ...update });
      return newMap;
    });
  }, []);

  /**
   * Migrates a single user's insecure photo to v1.5 (secure hash-based path)
   */
  const migrateInsecureToV15 = useCallback(async (
    userId: string,
    username: string,
    insecurePhotoUrl: string
  ): Promise<{ success: boolean; migrated: number; original: number }> => {
    // Declarar contadores no escopo da função para acessá-los no catch
    let migratedPhotoCount = 0;
    let originalPhotoCount = 0;
    
    try {
      updateProgress(userId, { username, status: 'analyzing', message: 'Analisando foto insegura...', progress: 10 });

      // 1. Detect version and variants
      const versionInfo = detectPhotoVersion(insecurePhotoUrl);
      debugLogger.info('PhotoMigrationV15', `Detected version for ${username}:`, versionInfo);

      // Se main_photo já é v1.5, pular download mas continuar para migrar profile_photos[]
      const shouldSkipMainPhoto = versionInfo.version === 'v1.5';
      if (shouldSkipMainPhoto) {
        debugLogger.info('PhotoMigrationV15', `main_photo_url already v1.5, checking profile_photos[] array`);
        updateProgress(userId, { status: 'processing', message: 'Verificando array de fotos...', progress: 20 });
      }

      // Detectar bucket e extrair file path (necessário para cleanup depois)
      const bucket = insecurePhotoUrl.includes('profile_photos') ? 'profile_photos' : 'chat_media';
      
      let filePath = '';
      if (insecurePhotoUrl.includes('/object/public/')) {
        filePath = insecurePhotoUrl.split('/object/public/')[1].split('?')[0];
      } else if (insecurePhotoUrl.includes('/object/sign/')) {
        filePath = insecurePhotoUrl.split('/object/sign/')[1].split('/')[1];
      }
      
      // Remove variantes semânticas do path se existir
      filePath = filePath
        .replace(/-large-display\.jpg$/, '.jpg')
        .replace(/-medium-card\.jpg$/, '.jpg')
        .replace(/-small-thumb\.jpg$/, '.jpg')
        .replace(/-micro-avatar\.jpg$/, '.jpg');
      
      // CRÍTICO: Strip bucket prefix para evitar duplicação (profile_photos/profile_photos/...)
      filePath = filePath
        .replace(/^profile_photos\//, '')
        .replace(/^chat_media\//, '');

      // Tentar variantes com signed URLs (prioridade: maior qualidade primeiro)
      const variantPaths = [
        filePath.replace('.jpg', '_3.jpg'),  // v1.5 numeric variant
        filePath.replace('.jpg', '-large-display.jpg'),  // v1 semantic
        filePath.replace('.jpg', '_2.jpg'),
        filePath.replace('.jpg', '-medium-card.jpg'),
        filePath.replace('.jpg', '_1.jpg'),
        filePath.replace('.jpg', '-small-thumb.jpg'),
        filePath.replace('.jpg', '_0.jpg'),
        filePath.replace('.jpg', '-micro-avatar.jpg'),
        filePath  // Fallback: foto principal sem sufixo
      ];

      let sourceBlob: Blob | null = null;
      let newMainPhotoUrl = insecurePhotoUrl; // Preservar URL v1.5 existente se já migrada
      let userHash = ''; // Declarar fora do if para uso posterior

      // 2. Find best source photo (highest quality variant) - SKIP se já v1.5
      if (!shouldSkipMainPhoto) {
        updateProgress(userId, { status: 'downloading', message: 'Baixando foto original...', progress: 20 });
        debugLogger.info('PhotoMigrationV15', `Extracting from bucket=${bucket}, path=${filePath}`);

        let sourceUrl = '';

        for (const variantPath of variantPaths) {
          try {
            debugLogger.info('PhotoMigrationV15', `Trying variant: ${variantPath}`);
            const signedUrl = await getSignedMediaUrl(variantPath, 3600, bucket);
          
          if (!signedUrl) {
            debugLogger.warn('PhotoMigrationV15', `No signed URL for: ${variantPath}`);
            continue;
          }

          // Fetch com timeout de 10s
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const response = await fetch(signedUrl, { signal: controller.signal });
          clearTimeout(timeoutId);
          
          if (response.ok) {
            sourceBlob = await response.blob();
            sourceUrl = variantPath;
            debugLogger.info('PhotoMigrationV15', `✅ Downloaded from: ${variantPath} (${(sourceBlob.size / 1024).toFixed(2)} KB)`);
            break;
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            debugLogger.warn('PhotoMigrationV15', `Timeout on variant: ${variantPath}`);
          } else {
            debugLogger.warn('PhotoMigrationV15', `Failed variant: ${variantPath}`, error);
          }
          continue;
        }
      }

        if (!sourceBlob) {
          throw new Error(`Nenhuma variante de foto encontrada para download. Bucket: ${bucket}, Path base: ${filePath}`);
        }

        // 3. Convert Blob to File
        const sourceFile = new File([sourceBlob], 'source.jpg', { type: 'image/jpeg' });

        // 4. Process image into 4 variants
        updateProgress(userId, { status: 'processing', message: 'Processando variantes...', progress: 40 });

        const imageVariants = await processImageToVariants(sourceFile);
        if (imageVariants.length === 0) {
          throw new Error('Falha ao processar variantes da imagem');
        }

        // 5. Generate secure paths for v1.5 (SINGLE imageHash for all variants)
        updateProgress(userId, { status: 'uploading', message: 'Gerando caminhos seguros...', progress: 60 });

        const { generateUserHash, generateSecureImageHash } = await import('@/utils/securePhotoNaming');
        userHash = await generateUserHash(userId);
        const imageHash = await generateSecureImageHash(Date.now());

        const securePaths = [0, 1, 2, 3].map(variant => ({
          path: `${userHash}/${imageHash}_${variant}.jpg`,
          imageHash,
          userHash
        }));

        // 6. Upload all 4 variants to secure paths
        const uploadPromises = imageVariants.map((variant, index) => {
          const securePath = securePaths[index].path;
          return supabase.storage
            .from('profile_photos')
            .upload(securePath, variant.file, {
              contentType: 'image/jpeg',
              upsert: false,
            });
        });

        const uploadResults = await Promise.all(uploadPromises);
        const failedUploads = uploadResults.filter(result => result.error);

        if (failedUploads.length > 0) {
          debugLogger.error('PhotoMigrationV15', 'Failed uploads:', failedUploads);
          throw new Error(`${failedUploads.length} uploads falharam`);
        }

        // 7. Update database with new secure main_photo_url
        updateProgress(userId, { status: 'updating', message: 'Atualizando banco de dados...', progress: 80 });

        newMainPhotoUrl = securePaths[3].path; // Variant 3 is main photo

        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            main_photo_url: newMainPhotoUrl,
            // NÃO atualizar photo_version aqui - será atualizado após migração do array
          })
          .eq('id', userId);

        if (updateError) {
          throw updateError;
        }
      } else {
        debugLogger.info('PhotoMigrationV15', `Skipping main_photo download (already v1.5)`);
        updateProgress(userId, { status: 'processing', message: 'main_photo já migrada, processando array...', progress: 50 });
      }

      // 7.5. Migrate profile_photos[] array (SEMPRE executa, independente de main_photo)
      updateProgress(userId, { status: 'updating', message: 'Migrando array de fotos...', progress: 85 });
      
      const { data: profileData } = await supabase
        .from('profiles')
        .select('profile_photos')
        .eq('id', userId)
        .single();

      if (profileData?.profile_photos && Array.isArray(profileData.profile_photos)) {
        originalPhotoCount = profileData.profile_photos.length;
        debugLogger.info('PhotoMigrationV15', `Found ${originalPhotoCount} photos in array, starting migration`);
        
        const migratedPhotos: string[] = [];
        
        for (const oldPhotoUrl of profileData.profile_photos) {
          // Detectar se é v1 inseguro ou v0/v0.5
          const versionInfo = detectPhotoVersion(oldPhotoUrl);
          
          // ✅ Validar URL antes de processar (mais rigoroso)
          const isInvalidUrl = !oldPhotoUrl || 
                               oldPhotoUrl.endsWith('/profile_photos') || 
                               oldPhotoUrl.endsWith('/chat_media') ||
                               !oldPhotoUrl.includes('.jpg');
          
          if (isInvalidUrl) {
            debugLogger.warn('PhotoMigrationV15', `Invalid or incomplete URL, skipping:`, { 
              url: oldPhotoUrl,
              reason: 'Missing filename or incomplete path'
            });
            continue;
          }

          // Detectar bucket específico para esta foto
          const arrayPhotoBucket = oldPhotoUrl.includes('profile_photos') ? 'profile_photos' : 'chat_media';
          debugLogger.info('PhotoMigrationV15', `Processing array photo:`, {
            url: oldPhotoUrl.substring(0, 80),
            bucket: arrayPhotoBucket,
            version: versionInfo.version
          });

          // ✅ Tentar apenas arquivo original para v1 (sem variantes)
          if (versionInfo.version === 'v1' || versionInfo.version === 'v0' || versionInfo.version === 'v0.5') {
            debugLogger.info('PhotoMigrationV15', `Detected v1 photo, attempting to download original file`);
            
            // Extrair path base da URL
            let arrayFilePath = '';
            if (oldPhotoUrl.includes('/object/public/')) {
              const publicPath = oldPhotoUrl.split('/object/public/')[1].split('?')[0];
              arrayFilePath = publicPath.replace(/^profile_photos\//, '').replace(/^chat_media\//, '');
            } else if (oldPhotoUrl.includes('/object/sign/')) {
              const signPath = oldPhotoUrl.split('/object/sign/')[1].split('?')[0];
              arrayFilePath = signPath.replace(/^profile_photos\//, '').replace(/^chat_media\//, '');
            }
            
            if (!arrayFilePath || !arrayFilePath.includes('.jpg')) {
              debugLogger.error('PhotoMigrationV15', `❌ Invalid file path extracted from URL:`, {
                url: oldPhotoUrl.substring(0, 80),
                extractedPath: arrayFilePath,
                reason: 'Missing filename or .jpg extension'
              });
              continue;
            }
            
            // ✅ CRÍTICO: Tentar apenas o arquivo original (sem variantes)
            let downloadedBlob: Blob | null = null;
            
            try {
              debugLogger.info('PhotoMigrationV15', `Attempting to download original v1 file:`, {
                bucket: arrayPhotoBucket,
                path: arrayFilePath
              });
              
              // Gerar signed URL para arquivo original
              const originalSignedUrl = await getSignedMediaUrl(arrayFilePath, 3600, arrayPhotoBucket);
              
              if (!originalSignedUrl) {
                throw new Error('Failed to generate signed URL');
              }
              
              // Download com timeout
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 10000);
              
              const response = await fetch(originalSignedUrl, { signal: controller.signal });
              clearTimeout(timeoutId);
              
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              
              // ✅ Sucesso!
              downloadedBlob = await response.blob();
              
              debugLogger.info('PhotoMigrationV15', `✅ Downloaded original v1 photo (${(downloadedBlob.size / 1024).toFixed(2)} KB)`);
              
            } catch (error: any) {
              // ✅ Detectar "arquivo não existe" explicitamente
              if (error.message?.includes('MediaNotFound') || error.message?.includes('Object not found')) {
                debugLogger.error('PhotoMigrationV15', `❌ Source v1 photo not found in storage:`, {
                  bucket: arrayPhotoBucket,
                  path: arrayFilePath,
                  reason: 'File was never uploaded or has been deleted',
                  classification: 'UNRECOVERABLE'
                });
              } else if (error.name === 'AbortError') {
                debugLogger.error('PhotoMigrationV15', `❌ Timeout downloading v1 photo`);
              } else {
                debugLogger.error('PhotoMigrationV15', `❌ Error downloading v1 photo:`, error);
              }
              
              // Não incrementar migratedPhotoCount (foto irrecuperável)
              continue;
            }
            
            if (!downloadedBlob) {
              debugLogger.error('PhotoMigrationV15', `❌ No valid file found for v1 photo, skipping`);
              continue;
            }
            
            // Processar variantes v1.5 e upload
            const photoFile = new File([downloadedBlob], 'photo.jpg', { type: 'image/jpeg' });
            const variants = await processImageToVariants(photoFile);
            
            // Se userHash não foi gerado, gerar agora
            if (!userHash) {
              userHash = await generateUserHash(userId);
            }
            
            // Gerar path seguro ÚNICO para esta foto
            const photoHash = await generateSecureImageHash(Date.now() + Math.random());
            const photoSecurePaths = [0, 1, 2, 3].map(v => ({
              path: `${userHash}/${photoHash}_${v}.jpg`
            }));
            
            // Upload todas as 4 variantes
            const uploadResults = await Promise.all(
              variants.map((variant, idx) =>
                supabase.storage
                  .from('profile_photos')
                  .upload(photoSecurePaths[idx].path, variant.file, { 
                    contentType: 'image/jpeg',
                    upsert: false 
                  })
                  .then(result => ({ success: !result.error, error: result.error, variant: idx }))
                  .catch(err => ({ success: false, error: err, variant: idx }))
              )
            );
            
            const successfulUploads = uploadResults.filter(r => r.success).length;
            
            if (successfulUploads === 4) {
              migratedPhotos.push(photoSecurePaths[3].path);
              migratedPhotoCount++;
              debugLogger.info('PhotoMigrationV15', `✅ v1 photo migrated successfully (${successfulUploads}/4 variants)`);
            } else {
              debugLogger.error('PhotoMigrationV15', `❌ Failed to upload all variants (${successfulUploads}/4)`);
            }
            
          } else if (versionInfo.version === 'v1.5') {
            // Já é v1.5, manter
            migratedPhotos.push(oldPhotoUrl);
            migratedPhotoCount++;
          }
        }
        
        // Validar sucesso completo antes de atualizar DB
        debugLogger.info('PhotoMigrationV15', `Migration summary: original=${originalPhotoCount}, migrated=${migratedPhotoCount}`);
        
        if (migratedPhotoCount === originalPhotoCount) {
          // Migração 100% bem-sucedida - atualizar DB com array completo E photo_version
          const { error: fullUpdateError } = await supabase
            .from('profiles')
            .update({ 
              profile_photos: migratedPhotos,
              photo_version: 'v1.5'
            })
            .eq('id', userId);
          
          if (fullUpdateError) {
            debugLogger.error('PhotoMigrationV15', 'Failed to update profile:', fullUpdateError);
            throw fullUpdateError;
          }
          
          debugLogger.info('PhotoMigrationV15', `✅ Full migration successful: ${migratedPhotoCount}/${originalPhotoCount} photos`);
          
        } else if (migratedPhotoCount > 0 && migratedPhotoCount < originalPhotoCount) {
          // Migração parcial - NÃO atualizar photo_version, apenas profile_photos[]
          debugLogger.warn('PhotoMigrationV15', `⚠️ Partial migration: ${migratedPhotoCount}/${originalPhotoCount} photos successful`);
          
          const { error: partialUpdateError } = await supabase
            .from('profiles')
            .update({ 
              profile_photos: migratedPhotos
              // NÃO atualizar photo_version - manter como estava
            })
            .eq('id', userId);
          
          if (partialUpdateError) {
            debugLogger.error('PhotoMigrationV15', 'Failed to save partial migration:', partialUpdateError);
          }
          
          throw new Error(`Partial migration: only ${migratedPhotoCount}/${originalPhotoCount} photos migrated successfully. User needs manual review.`);
          
        } else if (migratedPhotoCount === 0) {
          // Nenhuma foto migrada com sucesso
          throw new Error(`Migration failed: 0/${originalPhotoCount} photos migrated successfully`);
        }
      } else {
        // Se não há fotos no array, atualizar photo_version diretamente
        const { error: versionUpdateError } = await supabase
          .from('profiles')
          .update({ photo_version: 'v1.5' })
          .eq('id', userId);
        
        if (versionUpdateError) {
          debugLogger.warn('PhotoMigrationV15', 'Failed to update photo_version:', versionUpdateError);
        }
      }

      // 8. Delete old insecure files (SKIP se main_photo já era v1.5)
      if (!shouldSkipMainPhoto) {
        updateProgress(userId, { status: 'cleaning', message: 'Removendo arquivos antigos...', progress: 90 });

        const oldFilePaths = variantPaths.map(vPath => {
          // Extract file path from URL (remove base URL if exists)
          const match = vPath.match(/profile_photos\/(.+)$/);
          return match ? match[1] : vPath;
        }).filter(Boolean) as string[];

        if (oldFilePaths.length > 0) {
          const { error: deleteError } = await supabase.storage
            .from(bucket)
            .remove(oldFilePaths);

          if (deleteError) {
            debugLogger.warn('PhotoMigrationV15', 'Failed to delete old files:', deleteError);
            // Non-critical error, continue
          }
        }
      } else {
        debugLogger.info('PhotoMigrationV15', 'Skipping cleanup (main_photo already v1.5)');
      }

      updateProgress(userId, { status: 'completed', message: '✅ Migração concluída!', progress: 100 });
      debugLogger.info('PhotoMigrationV15', `Successfully migrated ${username} to v1.5`);

      // Retornar resultado detalhado
      const migrationResult = {
        success: true,
        migrated: migratedPhotoCount,
        original: originalPhotoCount
      };
      
      debugLogger.info('PhotoMigrationV15', `Migration result:`, migrationResult);
      return migrationResult;
      
    } catch (error) {
      debugLogger.error('PhotoMigrationV15', `Migration failed for ${username}:`, error);
      updateProgress(userId, { 
        status: 'error', 
        message: `❌ Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        progress: 0,
      });
      
      // Retornar resultado de falha com contagem correta
      return {
        success: false,
        migrated: 0,
        original: originalPhotoCount
      };
    }
  }, [processImageToVariants, updateProgress]);

  /**
   * Batch migration for multiple users
   */
  const migrateBatch = useCallback(async (
    users: Array<{ id: string; username: string; main_photo_url: string }>
  ): Promise<{ success: number; failed: number }> => {
    setIsProcessing(true);
    let successCount = 0;
    let failedCount = 0;

    for (const user of users) {
      const result = await migrateInsecureToV15(user.id, user.username, user.main_photo_url);
      if (result.success) {
        successCount++;
      } else {
        failedCount++;
      }
    }

    setIsProcessing(false);
    return { success: successCount, failed: failedCount };
  }, [migrateInsecureToV15]);

  const clearProgress = useCallback(() => {
    setProgress(new Map());
  }, []);

  return {
    isProcessing,
    progress,
    migrateInsecureToV15,
    migrateBatch,
    clearProgress,
  };
}
